import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

const Toast = ({ message, type, duration, onClose, actions }) => {

    useEffect(() => {
        if (duration && !actions) {
            const timer = setTimeout(onClose, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, actions, onClose]);

    const icons = {
        success: <CheckCircle className="w-5 h-5" />,
        error: <XCircle className="w-5 h-5" />,
        warning: <AlertCircle className="w-5 h-5" />,
        info: <Info className="w-5 h-5" />
    };

    const colors = {
        success: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400',
        error: 'bg-red-500/20 border-red-500/30 text-red-400',
        warning: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
        info: 'bg-blue-500/20 border-blue-500/30 text-blue-400'
    };

    /* =========================
       CONFIRMATION MODAL STYLE
    ========================= */

    if (actions) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed inset-0 flex items-center justify-center z-50"
            >
                <div className="w-[380px] rounded-2xl px-6 py-5 bg-teal-800 border border-gray-700 shadow-2xl">

                    {/* MESSAGE */}
                    <div className="flex items-start gap-3 mb-6">
                        <div className="mt-0.5">
                            {icons[type]}
                        </div>

                        <p className="text-base font-medium text-white leading-relaxed">
                            {message}
                        </p>
                    </div>

                    {/* BUTTONS */}
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => actions.onCancel && actions.onCancel()}
                            className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-sm text-white transition"
                        >
                            {actions.cancelText || 'Cancel'}
                        </button>

                        <button
                            onClick={() => actions.onConfirm && actions.onConfirm()}
                            className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm transition"
                        >
                            {actions.confirmText || 'Confirm'}
                        </button>
                    </div>

                </div>
            </motion.div>
        );
    }

    /* =========================
       NORMAL TOAST (TOP RIGHT)
    ========================= */

    return (
        <motion.div
            initial={{ opacity: 0, y: -40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`flex items-start gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm ${colors[type]}`}
        >
            <div className="mt-0.5">{icons[type]}</div>

            <div className="flex-1 text-sm font-medium">
                {message}
            </div>

            <button onClick={onClose}>
                <X className="w-4 h-4" />
            </button>
        </motion.div>
    );
};

/* =========================
   TOAST CONTAINER (UNCHANGED)
========================= */

export const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-md">
            <AnimatePresence>
                {toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        duration={toast.duration}
                        actions={toast.actions}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
};

export default Toast;